/* eslint-disable no-useless-escape */
/* eslint-disable import/prefer-default-export */
/* eslint-disable max-len */

import {
  ArchivedState,
  Case,
  Company, CompanyData, CountryCode,
  Currency,
  CurrencyPeriod,
  Employer, EmployerAdminAssignment, EmployerAssignment,
  FamilyRelationship, FileData, FileRequirement, FileType,
  ForeignNational,
  JobDescription,
  Money,
  NationalOnboardingSections,
  PersonType, PrimaryContact,
  ReportStatus, Role, StreetAddress, Task, TeamMember,
  USCISReceiptDetails,
} from 'API';
import { fetchAuthSession } from 'aws-amplify/auth';
import { client } from 'lib/graphql-client';
import {
  addMonths,
  format as formatDate,
  isSameDay,
  isSameYear,
  isValid,
  parseISO,
} from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import slugify from 'slugify';
import { KeyboardEvent, MouseEvent } from 'react';
import { intComma } from 'humanize-plus';
import { Relative } from 'services/national';
import {
  CustomTemplateFields,
  DATE_TIME_FORMAT,
  Field, HOME_TZ, NO_NAME, SLUGIFY_OPTIONS,
  ReceiptNoticesWithLiveCaseStatus,
} from './constants';
import {
  DateFilter,
  LoginStage, ReportOptions, TemplateCategory, UpcomingExpirations, WithinSubOperator, reportingFieldTypes, resolvers,
} from './enums';
import { countryCodeResolver, stateCodeResolver } from './geoResolvers';
import { AssociatedReports } from './propTypes';

/**
 * A utility function to plug into Array.sort() in order to
 * alphabetize an array of Companies or people. If the objects
 * are companies, it will alphabetize by name. If people, it will
 * alphabetize by lastname, then firstname.
 * @param {Company | Person} a
 * @param {Company | Person} b
 * @returns
 */
export const alphaByName = (a: Company | Person, b: Company | Person) => {
  if ((a.displayCompanyName || a.name || a.lowerCompanyName) && (b.displayCompanyName || b.name || b.lowerCompanyName)) {
    // We're sorting companies. Compare name only.
    return ((a.displayCompanyName || a.name || a.lowerCompanyName).toLowerCase() > ((b.displayCompanyName || b.name || b.lowerCompanyName)).toLowerCase()
      ? 1
      : -1);
  }

  // We're comparing people.
  if (a.lastname === b.lastname) {
    return (
      (a.firstname || 'zzz').toLowerCase() > (b.firstname || 'zzz').toLowerCase()
        ? 1
        : -1
    );
  }

  return (a.lastname || 'zzz').toLowerCase()
    > (b.lastname || 'zzz').toLowerCase()
    ? 1
    : -1;
};

export const ascendingCompany = (a: Company | Person, b: Company | Person) => {
  if ((a.company.displayCompanyName || a.company.name || a.company.lowerCompanyName) && (b.company.displayCompanyName || b.company.name || b.company.lowerCompanyName)) {
    // We're sorting companies. Compare name only.
    return ((a.company.displayCompanyName || a.company.name || a.company.lowerCompanyName)?.toLowerCase() > ((b.company.displayCompanyName || b.name || b.company.lowerCompanyName))?.toLowerCase()
      ? 1
      : -1);
  }

  // We're comparing people.
  if (a.lastname === b.lastname) {
    return (
      (a.firstname || 'zzz').toLowerCase() > (b.firstname || 'zzz').toLowerCase()
        ? 1
        : -1
    );
  }

  return (a.lastname || 'zzz').toLowerCase()
    > (b.lastname || 'zzz').toLowerCase()
    ? 1
    : -1;
};

export const descByName = (a: Company | Person, b: Company | Person) => {
  if ((a.displayCompanyName || a.name) && (b.displayCompanyName || b.name)) {
    // We're sorting companies. Compare name only.
    return ((a.displayCompanyName || a.name).toLowerCase() > ((b.displayCompanyName || b.name)).toLowerCase()
      ? -1
      : 1);
  }

  return (a.lastname || 'zzz').toLowerCase()
    > (b.lastname || 'zzz').toLowerCase()
    ? -1
    : 1;
};

export const alphaByCountry = (
  a: any, b: any,
) => {
  const countryA = countryCodeResolver(a);
  const countryB = countryCodeResolver(b);

  return (countryA.toLowerCase() > countryB.toLowerCase() ? 1 : -1);
};

/**
 * A utility function to check the person exist in admin companies and primary Contact companies.
 * if person exist the it return the company
 * @param { Person} person
 */
export const checkAdminAndPrimaryContactByCompany = (person: Person) => person?.adminCompanies?.items
  .filter((company: EmployerAdminAssignment) => (person?.primaryContactCompanies?.items
    .some((primaryCompany: PrimaryContact) => primaryCompany?.companyID === company?.companyID)));

export const alphaByFileName = (a: any, b: any) => {
  const nameA = a.fileName;
  const nameB = b.fileName;

  return nameA.toLowerCase() > nameB.toLowerCase() ? 1 : -1;
};

export const alphabyState = (a: any, b: any) => {
  const stateA = stateCodeResolver(a);
  const stateB = stateCodeResolver(b);

  return stateA.toLowerCase() > stateB.toLowerCase() ? 1 : -1;
};

export const alphaByTitle = (
  a: any, b: any,
) => ((a.title).toLowerCase() > (b.title).toLowerCase()
  ? 1
  : -1);

export const sortByOrder = (a: Task | null, b?: Task | null) => (
  (a?.orderIndex || 0) > (b?.orderIndex || 0) ? 1 : -1
);

export const sortByStartDate = (a?: Case | null, b?: Case | null) => ((a?.startDate || '') < (b?.startDate || '') ? 1 : -1);

export const sortByUploadDate = (
  a: FileData, b: FileData,
) => ((a.uploadDate || '') > (b.uploadDate || '') ? 1 : -1);

export const sortByTaskCategory = (a: Task, b: Task) => ((a.category || '') > (b.category || '') ? 1 : -1);

export const canViewFile = (contentType: string): boolean => {
  const isPDF = contentType === 'application/pdf';
  const isImage = contentType?.indexOf('image') === 0;

  return (isPDF || isImage);
};

export const capitalizeString = (string?: string): string => (string ? string.charAt(0).toUpperCase() + string.slice(1) : '');

// finds first propertyName containing "list" or "search" in its name from an object, e.g "customerlist"
export const getKeyFromData = (data: object): string => Object.keys(data)?.find(
  (key) => key?.includes('list') || key?.includes('search'),
) as string;

/**
 * A helper to create the payload necessary for API calls
 * @param body
 * @returns
 */
export const getPayload = async (body: any) => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString() ?? '';

  return {
    body,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    authMode: 'iam' as const,
  };
};

/**
 * Recursively paginates through a list query to retrieve
 * all matching results.
 * @param query GraphQL list query; *must* be a list query
 * @param variables An optional object of variables, such as filters or limit
 * @param items Running array of results
 * @returns
 */
export const fetchListAll = async (
  query: any,
  variables: any = {},
  items: Array<any> = [],
  maxCount?: number,
): Promise<Array<any>> => {
  const DEFAULT_LIMIT = 200;
  if (!variables.limit) {
    // eslint-disable-next-line no-param-reassign
    variables.limit = DEFAULT_LIMIT;
  }
  let data;

  try {
    // errorPolicy: 'all' preserves partial data on non-fatal errors (same behaviour as v3)
    const result = await (client.graphql({ query, variables, errorPolicy: 'all' } as any) as any);
    data = result.data;
    if (!data) throw new Error();
  } catch (error) {
    const key = getKeyFromData((error as any).data);
    if (key) {
      // Non-fatal error. Roll with it.
      data = (error as any).data;
    } else {
      // eslint-disable-next-line no-console
      console.log(error);
      throw new Error();
    }
  }

  const key = getKeyFromData(data);
  const res = data[key];

  items.push(...res.items);

  if (!res.nextToken && (!maxCount || items.length < maxCount)) return items;

  if (maxCount && items.length >= maxCount) {
    return items.slice(0, maxCount);
  }

  // eslint-disable-next-line no-param-reassign
  variables.nextToken = res.nextToken;

  return fetchListAll(query, variables, items, maxCount);
};

export const idMap = (x: any) => x.id;

export const emailIsInWhitelist = (
  email: string, domainWhitelist: Array<string>,
): boolean => domainWhitelist.length > 0
  && domainWhitelist.some(
    (whitelistedDomain) => {
      const domain = `${whitelistedDomain.replace(/@/g, '')}`;

      return (email.indexOf(`@${domain}`) > 0
        || email.indexOf(`.${domain}`) > 0);
    },
  );

export const isValidStreetAddress = (
  address?: StreetAddress | null,
): boolean => !!(
  address?.address
      && address?.city
      && (address?.state || address?.adminArea)
      && address?.postalCode
      && address?.country
);

export const isValidEmail = (email: string): boolean => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(String(email).toLowerCase());
};

export const isValidURL = (url: string): boolean => {
  const urlregex = /^(https?|ftp):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.(com|edu|gov|int|mil|net|org|biz|arpa|info|name|pro|aero|coop|museum|[a-zA-Z]{2}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/;

  return urlregex.test(url);
};

/**
 * Take a string and remove accents, diacritics and punctuation,
 * then lowercase it.
 */
export const lower = (string: string): string => string
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^0-9a-z ]/gi, '')
  .toLowerCase();

/**
 * Make a string safe to travel as an S3 `x-amz-meta-*` request header.
 *
 * The browser writes header values out one byte per code unit (ISO-8859-1)
 * while the SigV4 signer hashes them as UTF-8, so a single non-ASCII character
 * puts different bytes on the wire than the ones that were signed and S3
 * answers 403 SignatureDoesNotMatch. Code points above U+00FF never even get
 * that far — `fetch` rejects the header outright. Either way the upload dies,
 * which is why a file named "I-129 for Itzel Gomez.pdf" (with the accent) could
 * never be uploaded. Fold accents down to their base letter and replace
 * anything still outside printable ASCII; the untouched value is what goes in
 * the database record.
 */
export const asciiMetadataValue = (value: string): string => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\u0020-\u007e]/g, '_')
  .trim();

export const notNullFilter = (x: any) => x;

export const notArchived = (x: any) => x.archived !== ArchivedState.ARCHIVED;

export const possessive = (string: string): string => {
  const lastCharacter = string.trim().slice(-1);

  if (lastCharacter === 's') {
    return `${string.trim()}’`;
  }

  return `${string.trim()}’s`;
};

/**
 * Clones the current value of a record and strips it down to
 * only the fields that are being edited in a given section.
 *
 * @param {any} original Person, Company, or ProcessRequest
 * @param {Array<ToggleableField>} fields The fields of a given section
 * @returns
 */
export const prepEdits = (original: any, fields: Array<ToggleableField>) => {
  let edits: any = {};

  if (!original) {
    return original;
  }

  // Special case for Company
  if (fields.some((field) => field.subFieldName === 'data')) {
    edits = { data: {} };
    edits.data.id = original.data?.id;
  }

  edits.id = original.id;
  edits.companyDataId = original.companyDataId;

  fields.forEach((field) => {
    if (!field.immutable && field.fieldName) {
      if (field.type === 'checkbox') {
        field.options?.forEach((option) => {
          edits[option.fieldName] = original[option.fieldName];
        });
      } else if (field.subFieldName) {
        edits[field.subFieldName] = original[field.subFieldName];
      } else if (field.fieldContainer) {
        edits[field.fieldName] = original?.[field.fieldContainer]?.[field.fieldName];
      } else {
        edits[field.fieldName] = original[field.fieldName];
      }
    }
  });
  if (edits.data && fields.some((field) => field.fieldName === 'LegalNameOfUSCompany')) {
    return { ...edits, data: { ...edits.data, LegalNameOfUSCompany: original.name } };
  }

  if (fields.some((field) => field.fieldName === 'hasSSN') && original.usSSN && edits.hasSSN == null) {
    edits.hasSSN = true;
  }

  return edits;
};

/**
 * Useful for removing leading articles from titles for purposes
 * of proper alphabetization.
 * @param {string} str
 * @returns {string}
 */
export const removeArticles = (str: string):string => {
  const words = str.split(' ');
  if (words.length <= 1) return str;
  if (
    words[0] === 'a' || words[0] === 'the' || words[0] === 'an'
  ) { return words.splice(1).join(' '); }

  return str;
};

/**
 * Display the month and day, if current year, or month, day, and year.
 * @param {string} date - date to be formatted
 * @param {string} [shortFormat] - optional override of time format
 * @param {string} [longFormat] - optional override of day format
 * @returns {string}
 */
export const shortOrLongDate = (
  date: string | null | undefined, shortFormat?: string, longFormat?: string,
): string => {
  if (!date) return '—';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '—';

  if (isSameYear(parsed, new Date())) {
    return formatInTimeZone(parsed, 'UTC', shortFormat || 'M/d');
  }

  return formatInTimeZone(parsed, 'UTC', longFormat || 'M/d/yy');
};

/**
 * Display the time, if today, or date.
 * @param {string} time - time to be formatted
 * @param {string} [timeFormat] - optional override of time format
 * @param {string} [dayFormat] - optional override of day format
 * @returns {string}
 */
export const timeOrDate = (
  time: string,
  timeFormat?: string,
  dayFormat?: string,
): string => {
  const tzGuess = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parsed = parseISO(time);

  if (isSameDay(parsed, new Date())) {
    if (tzGuess === HOME_TZ) {
      return formatDate(parsed, timeFormat || "'Today at ' h:mm aaa");
    }

    // Indicate TZ if not in chicago
    return formatInTimeZone(parsed, tzGuess, timeFormat || "'Today at ' h:mm aaa zzz");
  }

  // Use formatInTimeZone with local TZ so the `z` token resolves to a TZ abbrev
  // (e.g. "CDT") rather than date-fns' "GMT-5".
  return formatInTimeZone(parsed, tzGuess, dayFormat || 'M/d/yy');
};

// Quick way to make slugs human-readable. Not for public-facing use.
// Use only for analytics.
export const slugToTitleCase = (slug: string): string => (slug || '')?.trim()?.replace(/-/g, ' ')?.toLowerCase()
  ?.split(' ')
  ?.map((word) => (word ? word?.replace(word[0], (word[0] || '')?.toUpperCase()) : ''))
  ?.join(' ')
  ?.trim();

export const typeToRole = (type?: PersonType): Role => {
  if (!type) {
    throw new Error();
  }

  switch (type) {
    case PersonType.Employer:
      return Role.EMPLOYER;

    case PersonType.ForeignNational:
      return Role.FOREIGNNATIONAL;

    case PersonType.TeamMember:
      return Role.TEAMMEMBER;

    default:
      throw new Error();
  }
};

/**
 * Dedupe an array of objects.
 * @param {Array<any>} array An array of objects that need to be deduped.
 * @param {string} key A key with to make comparisons. Defaults to 'id'.
 * @returns
 */
export const uniquesByKey = (
  array: Array<any>, key: string = 'id',
) => Array
  .from(new Set(array.map((a) => a[key])))
  .map((thisKey) => array.find((a) => a[key] === thisKey));

const DEFAULT_CACHE_TTL = 1000 * 60 * 10; // 10 minutes

/**
 *
 * @param key The key to use for local storage
 * @param value Value to save in local storage
 * @param expiration How long the cache should last, in epoch time
 * @returns
 */
export const setCacheWithExpiration = (
  key: string,
  value: any,
  expiration: number = Date.now() + DEFAULT_CACHE_TTL,
) => {
  // `item` is an object that contains the original value
  // as well as the time when it's supposed to expire
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const item = {
    value,
    expiration,
  };

  // eslint-disable-next-line no-console
  // console.log(item, '>item>');
  // sessionStorage.setItem(key, JSON.stringify(item));
};

/**
 * Fire the callback, then cache and return its return value.
 * @param key The key to use for local storage
 * @param callback
 * @param ttl How long the cache should last, in milliseconds.
 * @returns
 */
export const callbackAndCache = async (
  key: string,
  callback: () => any,
  ttl: number = DEFAULT_CACHE_TTL,
): Promise<any> => {
  const value = await callback();

  setCacheWithExpiration(key, value, Date.now() + ttl);

  return value;
};

/**
 *
 * @param key The key to use for local storage
 * @param callback Optional. If present, the return of this callback will be
 *                 returned and stored in the cache in the event that the cache
 *                 is either not present or expired.
 * @param ttl How long the cache should last, in milliseconds.
 * @returns
 */
export const getCacheWithExpiration = async (
  key: string,
  callback?: () => any,
  ttl: number = DEFAULT_CACHE_TTL,
) => {
  const itemStr = sessionStorage.getItem(key);

  // If the item doesn't exist, return either the
  // callback return or null
  if (!itemStr) {
    return callback ? callbackAndCache(key, callback, ttl) : null;
  }

  const item = JSON.parse(itemStr);

  // Compare the expiry time of the item with the current time
  if (Date.now() > item.expiration) {
    // If the item is expired, delete the item from storage
    // and return null
    sessionStorage.removeItem(key);

    return callback ? callbackAndCache(key, callback, ttl) : null;
  }

  return item.value;
};

export const resolveIsRequired = (field: ToggleableField, edits: any): boolean => (
  typeof field.isRequired === 'function' ? field.isRequired(edits) : !!field.isRequired
);

export const validateFields = (
  fields: Array<ToggleableField>, edits: any,
): boolean => {
  const fieldValidity = fields.map((field) => {
    if (!field.validator && !field.isRequired) {
      return true;
    }

    let value;
    if (field.subFieldName) {
      value = edits[field.subFieldName]
        ? edits[field.subFieldName][field.fieldName]
        : null;
    } else {
      value = edits[field.fieldName];
    }

    const isRequired = resolveIsRequired(field, edits);

    if (isRequired
      && (value == null || (field.type === 'string' && value.length === 0))) {
      return false;
    }

    if (!field.validator) {
      return true;
    }

    return field.validator(value).length === 0;
  });

  return fieldValidity.every((validity) => validity);
};

export const countriesAsList = (
  codes: Array<CountryCode>,
): string => {
  if (!codes || codes.length === 0) {
    return '';
  }

  let joiner = ', ';

  const sortedCodes = [...codes].sort(alphaByCountry);

  const fullnames = sortedCodes
    .map((country) => `${countryCodeResolver(country!)}`);

  // If any of the country names incude commas, separate with semicolons
  if (fullnames.some((name) => name.indexOf(',') > 0)) {
    joiner = '; ';
  }

  return fullnames.join(joiner);
};

export const indexOfTabMatchingSlug = (tabs: Array<Tab>, slug?: string) => {
  if (!slug) {
    return 0;
  }

  return tabs.some((tab: Tab) => slugify(tab.title, SLUGIFY_OPTIONS) === slug)
    ? tabs.findIndex(
      (tab: Tab) => slugify(tab.title, SLUGIFY_OPTIONS) === slug,
    )
    : 0;
};

/**
 *
 * @param {string} type - The type of file that needs to be validated against DOCUMENT_TYPE enum
 *
 * @returns {enum} DOCUMENT_TYPES
 */
export const validateFileType = (type: string) => {
  const entries = Object.keys(FileType);
  if (entries.includes(type)) {
    return type;
  }

  return FileType.OTHER;
};
/**
 * stage verification process to check if user is on email verification stage
 * and if he submits the verification code or not if he didn't submit the code
 * he cant leave that stage
 * @param stage:number this is current stage where exactly login stage is
 * @returns validated stage if user clear verification stage only then he can move next stage
 */

/**
 * Drops the persisted email-verification stage. The stage is sticky on purpose
 * so a refresh cannot skip verification, which means every exit from that
 * screen - verified code, an address MH already verified, or a dead session -
 * has to clear it explicitly or the user is stranded there for good.
 */
export const clearEmailVerificationStage = () => {
  localStorage.removeItem('currentStage');
  localStorage.removeItem('isEmailVerified');
};

export const initialLoginStage = (stage:LoginStage) => {
  let currentStage;
  if (stage === LoginStage.VERIFY_EMAIL) {
    localStorage.setItem('currentStage', LoginStage.VERIFY_EMAIL.toString());
    localStorage.setItem('isEmailVerified', LoginStage.IS_EMAIL_VERIFIED.toString());
    const persistedStage = localStorage.getItem('currentStage');
    if (persistedStage) {
      currentStage = +persistedStage;
    }
  } else if (localStorage.getItem('currentStage')) {
    currentStage = parseInt(localStorage.getItem('currentStage') || '', 10);
  } else {
    currentStage = stage;
  }

  return currentStage;
};

export const getNonUSFields = (fields: Array<ToggleableField>) => fields.filter((field) => !field?.isUSGovtOnly);

export const getDisplayCompanyName = (displayName: string | undefined | null, companyName: string | null | undefined) => (displayName || companyName);

export const validateStringForRegex = (value: string | null, validationRegex: RegExp): boolean => {
  if (typeof value !== 'string' || value.trim() === '') {
    return true;
  }

  return validationRegex.test(value);
};

export const groupReceiptsByReceiptNumber = (
  caseReceiptStatusDetails: USCISReceiptDetails[],
) => caseReceiptStatusDetails.reduce<Record<string, USCISReceiptDetails[]>>(
  (acc, receiptItem) => {
    const receiptNotice = receiptItem.receiptNumber as string;

    return {
      ...acc,
      [receiptNotice]: [...(acc[receiptNotice] || []), receiptItem],
    };
  },
  {},
);

export const getLiveCaseStatusReceiptNumbers = (receiptNumbers: ReceiptNumbers) => {
  const liveCaseStatusReceiptNumbers = receiptNumbers ? Object.fromEntries(
    Object.entries(receiptNumbers).filter(([key]) => ReceiptNoticesWithLiveCaseStatus.includes(key)),
  ) : {};

  return liveCaseStatusReceiptNumbers;
};

export const checkForDuplicateReceiptNumbers = (value: string | null): boolean => {
  if (!value) return false;
  const receipts = value.split(',').map((receipt) => receipt.trim()).filter((receipt) => receipt !== '');

  return new Set(receipts).size !== receipts.length;
};

export const getReceiptNumberValues = (receiptNumber: string): string[] => receiptNumber
  .split(',')
  .map((receipt) => receipt.trim().replace(/-/g, ''))
  .filter(Boolean)
  .filter((value, index, self) => self.indexOf(value) === index);

export const toTitleCase = (text: string) => text
  .split(' ')
  .map((word) => {
    if (!word) return ''; // handles extra spaces

    return word[0].toUpperCase() + word.slice(1);
  })
  .join(' ');

// Possible document types
// same as enums.graphql
const FILE_TYPES = {
  I_140_APPLICATION: 'I_140_APPLICATION',
  DS_160_COPY: 'DS_160_COPY',
  DOL_AUDIT_RESPONSE: 'DOL_AUDIT_RESPONSE',
  E_3_APPLICATION: 'E_3_APPLICATION',
  E_3D_APPLICATION: 'E_3D_APPLICATION',
  E_3D_CONSULAR_PACKET: 'E_3D_CONSULAR_PACKET',
  H_1B1_APPLICATION: 'H_1B1_APPLICATION',
  H_4_CONSULAR_PACKET: 'H_4_CONSULAR_PACKET',
  REQUEST_EVIDENCE_RESPONSE: 'REQUEST_EVIDENCE_RESPONSE',
  TD_APPLICATION: 'TD_APPLICATION',
  TD_BORDER_PACKET: 'TD_BORDER_PACKET',
  TD_CONSULAR_PACKET: 'TD_CONSULAR_PACKET',
  TN_APPLICATION: 'TN_APPLICATION',
  APPLICATION_COPY_EMPLOYEE: 'APPLICATION_COPY_EMPLOYEE',
  PREMIUM_PROCESSING_UPGRADE_FORMS: 'PREMIUM_PROCESSING_UPGRADE_FORMS',
  I_140_RECEIPT_NOTICE: 'I_140_RECEIPT_NOTICE',
  APPROVAL_NOTICE: 'APPROVAL_NOTICE',
  CERTIFIED_PERM: 'CERTIFIED_PERM',
  DOL_AUDIT_NOTICE: 'DOL_AUDIT_NOTICE',
  OTHER_NOTICES: 'OTHER_NOTICES',
  RECEIPT_NOTICE: 'RECEIPT_NOTICE',
  REQUEST_FOR_EVIDENCE: 'REQUEST_FOR_EVIDENCE',
  LABOR_CONDITION_APPLICATION_NOTICE_FILLING: 'LABOR_CONDITION_APPLICATION_NOTICE_FILLING',
  EVIDENCE_OF_PRIMARY_E3: 'EVIDENCE_OF_PRIMARY_E3',
  EVIDENCE_OF_PRIMARY_H1B: 'EVIDENCE_OF_PRIMARY_H1B',
  EVIDENCE_OF_PRIMARY_TN: 'EVIDENCE_OF_PRIMARY_TN',
  BIRTH_CERTIFICATE: 'BIRTH_CERTIFICATE',
  DIPLOMA: 'DIPLOMA',
  EAD_CARD: 'EAD_CARD',
  EDUCATION_EVALUATION: 'EDUCATION_EVALUATION',
  EXPERIENCE_LETTER: 'EXPERIENCE_LETTER',
  H_4_APPLICATION: 'H_4_APPLICATION',
  I_140_APPROVAL_NOTICE: 'I_140_APPROVAL_NOTICE',
  I_20_COPY: 'I_20_COPY',
  I_797_APPROVAL_NOTICE: 'I_797_APPROVAL_NOTICE',
  I_94: 'I_94',
  MARRIAGE_CERTIFICATE: 'MARRIAGE_CERTIFICATE',
  MASTER_AGREEMENT: 'MASTER_AGREEMENT',
  OTHER: 'OTHER',
  PASSPORT: 'PASSPORT',
  PASSPORT_PHOTO: 'PASSPORT_PHOTO',
  PAY_STUBS: 'PAY_STUBS',
  PERM_APPLICATION: 'PERM_APPLICATION',
  USCIS_PRIMARY_APPLICATION: 'USCIS_PRIMARY_APPLICATION',
  USCIS_DEPENDENT_APPLICATION: 'USCIS_DEPENDENT_APPLICATION',
  EVIDENCE_OF_PRIMARY_USCIS: 'EVIDENCE_OF_PRIMARY_USCIS',
  PREVIOUS_I_140_APPROVAL_NOTICES: 'PREVIOUS_I_140_APPROVAL_NOTICES',
  PREVIOUS_I_797_APPROVAL_NOTICES: 'PREVIOUS_I_797_APPROVAL_NOTICES',
  PRIOR_EAD: 'PRIOR_EAD',
  RESUME: 'RESUME',
  SCHOOL_MARK_TRANSCRIPT: 'SCHOOL_MARK_TRANSCRIPT',
  SIGNED_EMPLOYMENT_CONTRACT: 'SIGNED_EMPLOYMENT_CONTRACT',
  SUPPORT_LETTER: 'SUPPORT_LETTER',
  VISA_STAMPS: 'VISA_STAMPS',
  W2: 'W2',
  H_1B_APPLICATION: 'H_1B_APPLICATION',
  // Extra Documents
  PRIOR_I_20: 'PRIOR_I_20',
  MOST_RECENT_W_2: 'MOST_RECENT_W_2',
  BIRTH_CERTIFICATE_OR_MARRIAGE_CERTIFICATE: 'BIRTH_CERTIFICATE_OR_MARRIAGE_CERTIFICATE',
  DEPENDENT_VISA_USCIS_SIGNATURE_PAGES: 'DEPENDENT_VISA_USCIS_SIGNATURE_PAGES',
  H_1B1_APPROVAL_NOTICE: 'H_1B1_APPROVAL_NOTICE',
  H_1B1_RECEIPT_NOTICE: 'H_1B1_RECEIPT_NOTICE',
  EAD_COPIES: 'EAD_COPIES',
  H_4_RECEIPT_NOTICE: 'H_4_RECEIPT_NOTICE',
  H_1B_RECEIPT_NOTICE: 'H_1B_RECEIPT_NOTICE',
  OTHER_PUBLIC_ACCESS_FILE: 'OTHER_PUBLIC_ACCESS_FILE',
  // EAD DOCTYPE
  EAD_SIGNATURE_PAGES: 'EAD_SIGNATURE_PAGES',
  EAD_APPLICATION: 'EAD_APPLICATION',
  H1B_LOTTERY_SELECTION_NOTICE: 'H1B_LOTTERY_SELECTION_NOTICE',
  // PERM Recruitment
  CANDIDATE_RESUMES_AND_ASSESSMENTS: 'CANDIDATE_RESUMES_AND_ASSESSMENTS',
  SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF: 'SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF',
  EMPLOYEE_REFERRAL_PROGRAM: 'EMPLOYEE_REFERRAL_PROGRAM',
  SUNDAY_NEWSPAPER_AD_1: 'SUNDAY_NEWSPAPER_AD_1',
  SUNDAY_NEWSPAPER_AD_2: 'SUNDAY_NEWSPAPER_AD_2',
  STATE_WORKFORCE_AGENCY_POSTING: 'STATE_WORKFORCE_AGENCY_POSTING',
  WEEKDAY_ETHNIC_NEWSPAPER_AD: 'WEEKDAY_ETHNIC_NEWSPAPER_AD',
  RADIO_AD: 'RADIO_AD',
  CAMPUS_PLACEMENT: 'CAMPUS_PLACEMENT',
  JOB_FAIR: 'JOB_FAIR',
  COMPANY_WEBSITE: 'COMPANY_WEBSITE',
  JOB_SEARCH_ENGINE: 'JOB_SEARCH_ENGINE',
  GREEN_CARD: 'GREEN_CARD',
  PREVAILING_WAGE_DETERMINATION: 'PREVAILING_WAGE_DETERMINATION',
  ATTORNEY_CLIENT_AGREEMENT: 'ATTORNEY_CLIENT_AGREEMENT',
  ADVANCED_PAROLE: 'ADVANCED_PAROLE',
  RE_ENTRY_PERMIT: 'RE_ENTRY_PERMIT',
  DS_2019: 'DS_2019',
};

export const nationalFileRequirements = [
  {
    type: FILE_TYPES.DIPLOMA,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EDUCATION_EVALUATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.SCHOOL_MARK_TRANSCRIPT,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EXPERIENCE_LETTER,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.W2,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PAY_STUBS,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.SIGNED_EMPLOYMENT_CONTRACT,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.OTHER,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: false,
  },
  {
    type: FILE_TYPES.BIRTH_CERTIFICATE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.MARRIAGE_CERTIFICATE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PASSPORT,
    assignedToID: null,
    expirationDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: false,
  },
  {
    type: FILE_TYPES.PASSPORT_PHOTO,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.RESUME,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EAD_CARD,
    assignedToID: null,
    expirationDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PRIOR_EAD,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_20_COPY,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_140_APPROVAL_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PREVIOUS_I_140_APPROVAL_NOTICES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_797_APPROVAL_NOTICE,
    assignedToID: null,
    expirationDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: false,
  },
  {
    type: FILE_TYPES.PREVIOUS_I_797_APPROVAL_NOTICES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_94,
    assignedToID: null,
    expirationDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
    durationOfStatus: false,
  },
  {
    type: FILE_TYPES.VISA_STAMPS,
    assignedToID: null,
    expirationDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.SUPPORT_LETTER,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_1B_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_4_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PERM_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.E_3_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EVIDENCE_OF_PRIMARY_USCIS,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.USCIS_DEPENDENT_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.USCIS_PRIMARY_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.E_3D_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.E_3D_CONSULAR_PACKET,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_1B1_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_4_CONSULAR_PACKET,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.TD_BORDER_PACKET,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.TD_CONSULAR_PACKET,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.TN_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_140_RECEIPT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.APPROVAL_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.CERTIFIED_PERM,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.DOL_AUDIT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: true,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.OTHER_NOTICES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.RECEIPT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EVIDENCE_OF_PRIMARY_E3,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.I_140_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.DS_160_COPY,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.DOL_AUDIT_RESPONSE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.REQUEST_EVIDENCE_RESPONSE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.TD_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.DEPENDENT_VISA_USCIS_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.REQUEST_FOR_EVIDENCE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: true,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EVIDENCE_OF_PRIMARY_H1B,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EVIDENCE_OF_PRIMARY_TN,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  // Extra Documents
  {
    type: FILE_TYPES.H_1B_RECEIPT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_4_RECEIPT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PRIOR_I_20,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_1B1_APPROVAL_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H_1B1_RECEIPT_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EAD_COPIES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  // EAD
  {
    type: FILE_TYPES.EAD_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.EAD_APPLICATION,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.H1B_LOTTERY_SELECTION_NOTICE,
    assignedToID: null,
    expirationDateRequired: false,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.GREEN_CARD,
    assignedToID: null,
    expirationDateRequired: true,
    issueDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.PREVAILING_WAGE_DETERMINATION,
    assignedToID: null,
    expirationDateRequired: true,
    issueDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.ADVANCED_PAROLE,
    assignedToID: null,
    expirationDateRequired: true,
    issueDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.DS_2019,
    assignedToID: null,
    expirationDateRequired: true,
    issueDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
  {
    type: FILE_TYPES.RE_ENTRY_PERMIT,
    assignedToID: null,
    expirationDateRequired: true,
    issueDateRequired: true,
    dueDateRequired: false,
    isApproved: false,
    hidden: true,
  },
];

export const requireExpirationDate = ['VISA_STAMPS', 'I_797_APPROVAL_NOTICE', 'CERTIFIED_LABOR_CONDITION_APPLICATION', 'SIGNED_CERTIFIED_LCA', 'PASSPORT', 'EAD_CARD', 'I_94', 'GREEN_CARD'];

export const fetchUniqueObjectsByType = (fileUnique: FileRequirement[]) => {
  const uniqueIds = new Set();

  return fileUnique?.filter((obj: FileRequirement) => {
    if (uniqueIds.has(obj.type)) {
      return false; // skip duplicate ID objects
    }
    uniqueIds.add(obj.type);

    return true;
  });
};

export const checkNullObject = (foreignNational: ForeignNational[]) => foreignNational
  .filter((item) => item !== null && typeof item === 'object')
  .map((item) => item as ForeignNational);

export const isRelativeAdmin = (person: ForeignNational | null | undefined) => (
  person?.relativesAsSource?.items ?? []
).filter((fn: FamilyRelationship | null) => fn?.target?.relativeAdmins
  ?.find((id: string) => id === person?.id)) as Array<FamilyRelationship>;

export const checkSectionsCompleted = (sections: NationalOnboardingSections): boolean => Object.values(sections).every((value) => !!value);

const compareArrays = (oldCompany: string[], newCompany: string[]) => oldCompany.length === newCompany.length
  && oldCompany.every((element: string, index: number) => element === newCompany[index]);

export const findChangedKeys = (oldCompanyData: CompanyData, newCompanyData: CompanyData): string[] => {
  const keys: string[] = Object.keys(oldCompanyData);
  const changedKeys: string[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    const companyKey = key as unknown as keyof CompanyData;

    if (JSON.stringify(oldCompanyData[companyKey]) !== JSON.stringify(newCompanyData[companyKey]) && companyKey !== 'updatedAt') {
      changedKeys.push(key);
    }
  }

  return changedKeys;
};

export const findChangedCompanyKeys = (oldCompany: Company, newCompany: Company): string[] => {
  const keys: string[] = Object.keys(oldCompany);
  const changedKeys: string[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    const companyKey = key as unknown as keyof Company;

    if (Array.isArray(oldCompany[companyKey])) {
      const changeArray = !compareArrays(oldCompany[companyKey] as string[], newCompany[companyKey] as string[]);
      if (changeArray)changedKeys.push(key);
    } else if (JSON.stringify(oldCompany[companyKey]) !== JSON.stringify(newCompany[companyKey])) {
      changedKeys.push(key);
    }
  }

  return changedKeys;
};

export const FIELDS_ENUM = {
  LOGO: 'logo',
  PRIMARY_ADDRESS: 'addressPrimary',
  SECONDARY_ADDRESS: 'addressSecondary',
  GROSS_REVENUE_FOR_MOST_RECENT: 'grossRevenueForMostRecentFiscalYear',
  NET_REVENUE_FOR_MOST: 'netRevenueForMostRecentFiscalYear',
  EMPLOYEES_EMAIL_MH_ATTORNEY_AND_PARALEGALS_DIRECTLY: 'employeesEmailMHAttorneysAndParalegalsDirectly',
  UTILIZE_PREMIUM_PROCESSING_SERVICE: 'utilizePremiumProcessingService',
  PREFERENCE_FOR_EMPLOYEES_TO_PAY_PART_OF_GREEN_CARD: 'preferenceForEmployeesToPayPartOfGreenCard',
  EDUCATION_EVALUATION: 'educationEvaluation',
  PAYMENT_PREFERENCE: 'paymentPreference',
  SHIP_ORIGINAL_APPROVAL_NOTICE: 'shipOriginalApprovalNotices',
  TECHNOLOGY_RELEASE_LICENSE_REQUIREMENT: 'technologyReleaseLicenseRequirement',
  EMPLOYEE_500_MORE: 'employs50OrMore',
  USES_APPLICATION_TRACKING_SYSTEM: 'usesApplicantTrackingSystem',
  ALLOW_EMPLOYEES_TO_TELECOMMUTE: 'allowsEmployeesToTelecommute',
  USES_HRS_SYSTEM: 'usesHRSSystem',
  MARTIAL_STATUS: 'maritalStatus',
  ADDITIONAL_FIRST_NAME: 'additionalFirstnames',
  ADDITIONAL_MIDDLE_NAME: 'additionalMiddlenames',
  ADDITIONAL_LAST_NAME: 'additionalLastnames',
  PASSPORT_GENDER: 'passportGender',
  US_RESIDENTIAL_ADDRESS: 'usResidentialAddress',
  US_MAILING_ADDRESS: 'usMailingAddress',
  LAST_ADDRESS_OUTSIDE_US: 'lastAddressOutsideUS',
  WORK_LOCATION: 'workLocation',
  OFFERED_SALARY: 'offeredSalary',
  IMMIGRATION_STATUS: 'immigrationStatus',
  HAS_APPROVED_I40: 'hasApprovedI140',
  IS_IN_REMOVAL_PROCEEDING: 'isInRemovalProceedings',
  HAS_HAD_I485: 'hasHadI485Filed',
  CRIMINAL_HISTORY: 'criminalHistory',
  J1J2: 'j1j2',
  HAS_BEEN_EMPLOYED_SINCE_LAST_ADMITTED: 'hasBeenEmployedSinceLastAdmitted',
  LOCATION: 'location',
  SALARY: 'salary',
  START_DATE: 'startDate',
  PREMIUM_PROCESSING: 'premiumProcessing',
  UPLOADED_DESCRIPTION_ID: 'uploadedDescriptionID',
  BIRTH_COUNTRY: 'birthCountry',
  CITIZEN_SHIP_COUNTRY: 'citizenshipCountry',
  COUNTRY_NAME_SHOW_TO_FN: 'companyNameShowToFN',
  BIRTH_DATE: 'birthDate',
  VISA_ISSUE_DATE: 'visaIssueDate',
  LEGAL_NAME_OF_US_COMPANY: 'LegalNameOfUSCompany',
  DOING_BUSINESS_AS: 'doingBusinessAs',
  FULL_TIME_EMPLOYEE_IN_US: 'fullTimeEmployeesInUS',
  DOMAIN_WHITE_LIST: 'domainWhitelist',
  PERMISSION_RELEASE_9089: 'permissionRelease9089',
  PERMISSION_RELEASE_RFE: 'permissionReleasePetitionsRFE',
  OTHER_VISA_STATUS_SPONSORED: 'other_visa_statuses_sponsored',
  USES_E_VERIFY: 'usesEVerify',
  MAINTAIN_WRITTEN_IMMIGRATION: 'maintainsAWrittenImmigrationPolicy',
  CURRENT_NUMBER_OF_EMPLOYEES_ON_L_VISA: 'current_number_of_employees_on_L_visas',
  CURRENT_NUMBER_OF_H1B_EMP: 'current_number_of_H1B_employees',
  I9_AUDIT_HAS_BEEN_COMPLETED: 'I9_audit_has_been_completed_in_past_18_months',
  USE_WAGE_SURVEY: 'usesWageSurvey',
  PLACES_EMPLOYEE_CLIENT_SIDE_MORE_THAN_COUPLE_OF_DAYS: 'placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime',
  PLACES_EMPLOYEE_CLIENT_SIDE_MORE_THAN_30_DAYS: 'placesEmployeesAtClientSitesForMoreThan30DaysAtATime',
  STAFF_EMPLOYEES_THIRD_PARTY_CLIENT_SIDE: 'staffsEmployeesAtThirdPartyEndClientSites',
  COMPANY_COVER_EXPENSE_OF_DEPENDENT_PETITION: 'companyCoversExpenseOfDependentPetitions',
  COMPANY_COVER_EXPENSE_OF_DEPENDENT_PETITION_AND_EAD: 'companyCoversExpenseOfDependentPetitionsAndOrEADCards',
  PART_TIME_EMPLOYEES_IN_US: 'partTimeEmployeesInUS',
  IS_H1B_DEPENDENT_EMPLOYER_F_3: 'isH1BDependentEmloyer',
  IS_NON_PROFIT_RESEARCH: 'isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3',
  IS_HIGHER_ED_NON_PROFIT_B: 'isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B',
  IS_HIGHER_ED_NON_PROFIT_F: 'isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2',
  HAS_BEEN_WILL_FULL_VIOLATOR: 'hasBeenWillfulViolator',
  IS_QUALIFY_CAMP_CAP_EXEMPT: 'isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4',
  IS_HIGHER_EAD_20: 'isHigherEd_20_USC_1001_a',
  IS_NON_PROFIT_RESEARCH_C: 'isNonProfitResearch_8_CFR_214_2_h_19_iii_C',
  REQUIRED_PHD: 'requiresPhd',
  REQUIRED_MASTER: 'requiresMasters',
  REQUIRED_BACHELOR: 'requiresBachelors',
  SPOUSE: 'spouse',
  PASSPORT_COUNTRY: 'passportCountry',
  PASSPORT_ISSUE_DATE: 'passportIssueDate',
  NIV_MAX_OUT: 'nivMaxout',
  PASSPORT_EXPIRATION_DATE: 'passportExpirationDate',
  HAS_NON_US_PASSPORT: 'hasNonUSPassport',
  LAST_ENTRY_US: 'lastEntryUS',
  HAS_HAD_VISA_PETITION_REVOKED_OR_DENIED: 'hasHadVisaPetitionRevokedOrDenied',
  HAS_PARTICIPATED_IN_ARMED_GROUP: 'hasParticipatedInArmedGroup',
  HAS_DETAILED_PERSON: 'hasDetailedPerson',
  HAS_PENDING_USCIS_APPLICATION: 'hasPendingUSCISApplication',
  HAS_PENDING_ETA: 'hasPendingETA9089',
  HAS_BEEN_ARRESTED_SINCE_LAST_US_ENTRY: 'hasBeenArrestedSinceLastUSEntry',
  HAS_VALID_OR_EXPIRED_EAD: 'hasValidOrExpiredEAD',
  HAS_USED_WEAPON: 'hasUsedWeapons',
  IMMIGRANT_PETITION_EVER_FIELD: 'immigrantPetitionEverFiled',
  HAS_CRIMINAL_OFFENSE: 'hasCriminalOffence',
  HAS_WEAPON_TRAINING: 'hasWeaponsTraining',
  HAS_TRANSPORT_WEAPON: 'hasTransportedWeapons',
  HAS_VIOLATED_VISA_TERMS: 'hasViolatedVisaTerms',
  IMMIGRANT_VISA_APPLICATION: 'immigrantVisaApplicant',
  JOB_DESCRIPTION_ID: 'jobDescriptionID',
  VISA_EXPIRATION: 'visaExpiration',
  BIRTH_STATE: 'birthState',
  NUMBER_OF_CHILDREN: 'dependents',
  NON_IMMIGRANT_VISA_NUMBER: 'nonImmigrantVisaNumber',
  VISA_CONSULATE: 'visaConsulate',
  TAX_TRACKING_F1J1: 'taxTrackingF1J1',
  FIRST_ENTRY_US: 'firstEntryUS',
  FIRST_NAME: 'firstname',
  LAST_NAME: 'lastname',
  MIDDLE_NAME: 'middlename',
  PERSONAL_EMAIL: 'emailPersonal',
  PHONE_NUMBER: 'phone',
  PASSPORT_NUMBER: 'passportNumber',
  EDUCATION_EVALUATION_NEEDED: 'educationEvaluationNeeded',
  STEM_EXT_ELIGIBLE: 'stemExtEligible',
  CAP_GAP: 'capGap',
  WAGE_ISSUES: 'wageIssues',

};

export const findChangedPersonKeys = (oldPerson: Person, newPerson: Person): string[] => {
  const keys: string[] = Object.keys(newPerson);
  const changedKeys: string[] = [];

  keys?.map((key) => {
    const personKey = key as unknown as keyof Person;

    if (JSON.stringify(oldPerson[personKey]) !== JSON.stringify(newPerson[personKey])) {
      changedKeys.push(key);
    }

    return key;
  });

  return changedKeys;
};

export const findChangedCaseKeys = (oldCaseEmployment: EmployerAssignment, newCaseEmployment: EmployerAssignment): string[] => {
  const keys: string[] = Object.keys(newCaseEmployment);
  const changedKeys: string[] = [];

  keys?.map((key) => {
    const caseKey = key as unknown as keyof EmployerAssignment;

    if (JSON.stringify(oldCaseEmployment[caseKey]) !== JSON.stringify(newCaseEmployment[caseKey])) {
      changedKeys.push(key);
    }

    return key;
  });

  return changedKeys;
};

export const findChangedDescriptionKeys = (olJobDescription: JobDescription, newJobDescription: JobDescription): string[] => {
  const keys: string[] = Object.keys(newJobDescription);
  const changedKeys: string[] = [];

  keys?.map((key) => {
    const jobKey = key as unknown as keyof JobDescription;

    if (JSON.stringify(olJobDescription[jobKey]) !== JSON.stringify(newJobDescription[jobKey])) {
      changedKeys.push(key);
    }

    return key;
  });

  return changedKeys;
};

export const showArrayValueInString = (name: [string]) => ` ${name[0]}`;

export const isValidDate = (dateString: string) => {
  const date = new Date(dateString);

  return date.toString() !== 'Invalid Date';
};

export const UpcomingExpirationsResolver = (expiration: UpcomingExpirations) => {
  switch (expiration) {
    case UpcomingExpirations.ALL_EXPIRATIONS:
      return 'All Expirations';
    case UpcomingExpirations.EXPIRING_WITHIN_7_MO:
      return 'Expiring Within 7 Mo.';
    case UpcomingExpirations.EXPIRING_WITHIN_3_MO:
      return 'Expiring Within 3 Mo.';
    case UpcomingExpirations.EXPIRING_WITHIN_1_MO:
      return 'Expiring Within 1 Mo.';
    default:
      return expiration;
  }
};

export const ReportOptionsResolver = (option: ReportOptions) => {
  switch (option) {
    case ReportOptions.ALL_CLIENTS:
      return 'All Clients';
    case ReportOptions.ONLY_MY_CLIENTS:
      return 'Only My Clients';
    default:
      return option;
  }
};

export const complianceOptions = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
];

export const complianceFields = {
  WAGE_ISSUES: 'Any Wage Issue(s) for Worksite Location(s)',
  EDUCATION_EVALUATION_NEEDED: 'Education Evaluation Needed',
  STEM_EXT_ELIGIBLE: 'STEM Ext Eligible',
  CAP_GAP: 'Cap Gap',
};

const getDateRange = (month: number) => {
  const today = new Date();
  const startDate = formatDate(today, 'yyyy-MM-dd');
  const endDate = formatDate(addMonths(today, month), 'yyyy-MM-dd');

  return { startDate, endDate };
};

export const getFilterDateRange = (expiration: UpcomingExpirations) => {
  switch (expiration) {
    case UpcomingExpirations.ALL_EXPIRATIONS:
      return null;
    case UpcomingExpirations.EXPIRING_WITHIN_7_MO:
      return getDateRange(7);
    case UpcomingExpirations.EXPIRING_WITHIN_3_MO:
      return getDateRange(3);
    case UpcomingExpirations.EXPIRING_WITHIN_1_MO:
      return getDateRange(1);
    default:
      return null;
  }
};

export const getField = (
  customTemplate: Array<CustomTemplateFields>, field: Field,
) => customTemplate.filter((customField) => customField.fieldName === field.fieldName)[0];

export const sortedDesc = (data: any) => data.sort((a: any, b: any) => Number(new Date(b?.createdAt)) - Number(new Date(a?.createdAt)));

export const ReportStatusResolver = (status: ReportStatus) => {
  switch (status) {
    case ReportStatus.IN_PROGRESS:
      return 'Pending';
    case ReportStatus.COMPLETE:
      return 'Completed';
    case ReportStatus.FAIL:
      return 'Failed';
    case ReportStatus.IN_COMPLETE:
      return 'Loading';
    default:
      return status;
  }
};

export const sortFieldsInOrder = (firstField: CustomTemplateFields, secondField: CustomTemplateFields) => {
  if (secondField.orderNo > firstField.orderNo) return -1;
  if (secondField.orderNo < firstField.orderNo) return 1;

  return 0;
};

export const getDefaultReportName = () => `${formatDate(new Date(), DATE_TIME_FORMAT)} Report`;

export const getAccessibleIds = (
  saveAsTemplate: boolean, personId: string,
  selectedEmployers: Employer[], selectedMH: TeamMember[],
) => {
  let accessibleIds: Array<string> = [];
  let employerIds: Array<string> = [];
  let mhIds: Array<string> = [];

  if (selectedEmployers?.length) {
    employerIds = selectedEmployers.map((employer: Employer) => employer.id);
  }
  if (selectedMH?.length) {
    mhIds = selectedMH.map((mh: TeamMember) => mh.id);
  }

  accessibleIds = [...employerIds, ...mhIds];
  if (personId && accessibleIds.filter((id) => id === personId)?.length === 0) accessibleIds.push(personId);

  return !saveAsTemplate ? accessibleIds : [];
};

export const blockEnter = (e: KeyboardEvent<HTMLDivElement>) => ((e.key === 'Enter') && e.preventDefault());

export const blockInvalidChar = (e: KeyboardEvent<HTMLDivElement>) => ['e', 'E', '+', '-', 'Enter', '.'].includes(e.key) && e.preventDefault();

export const convertOffsetToTimezone = (offsetString: string): string | null => {
  const match = offsetString.match(/([+-])(\d+):(\d+)/);

  if (!match) {
    return null;
  }

  // No deterministic offset→IANA-name mapping; preserves prior behavior
  // (moment.tz.zone(numericString) likewise returned null).
  return null;
};

export const convertTimezoneToOffset = (value: string) => {
  const utcOffsetMinutes = getTimezoneOffset(value, new Date()) / 60_000;

  const hours = Math.floor(Math.abs(utcOffsetMinutes) / 60);
  const minutes = Math.abs(utcOffsetMinutes) % 60;
  const offsetString = `UTC${utcOffsetMinutes >= 0 ? '+' : '-'}${hours}:${minutes}`;

  return offsetString;
};

export const getMostRecentId = (reports: AssociatedReports[]) => {
  if (reports.length === 0) return null;

  // Sort the reports by createdAt in descending order
  const sortedReports = reports.sort((a: AssociatedReports, b: AssociatedReports) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    return dateB - dateA;
  });

  // Return the id of the first (most recent) report
  return sortedReports[0];
};

export const shouldAddCustomNumberValue = (subOperator: string) => {
  const subOperators = [...Object.keys(WithinSubOperator), ...Object.keys(DateFilter)].filter(
    (operator) => operator !== WithinSubOperator.EXACT_DATE_RANGE
    && operator !== DateFilter.EXACT_DATE && operator !== DateFilter.TODAY,
  );

  return !!subOperators.includes(subOperator);
};

export const stopPropagation = (event: MouseEvent<HTMLElement, globalThis.MouseEvent>) => event.stopPropagation();

export const ReportingFieldsResolver = (fieldName: string, label: string, category: TemplateCategory | null | undefined) => {
  if (!category) return label;

  const isCase = category === TemplateCategory.Case;
  const isCompany = category === TemplateCategory.Company;

  switch (fieldName) {
    case 'companyLegalName':
    case 'displayCompanyName':
    case 'companyDbaName':
      if (isCompany) {
        return label;
      }

      return isCase ? `Sponsoring ${label}` : `Current ${label}`;
    default:
      return label;
  }
};

export const reportingFieldsAppendLabel = (fields: CustomTemplateFields[], category: TemplateCategory) => {
  if (!fields?.length) return fields;
  const isCase = category === TemplateCategory.Case;
  const isCompany = category === TemplateCategory.Company;

  const updatedFields: CustomTemplateFields[] = fields.map((field: CustomTemplateFields) => {
    const { fieldName, label } = field;
    switch (fieldName) {
      case 'companyLegalName':
      case 'displayCompanyName':
      case 'companyDbaName':
        return {
          ...field,
          label: isCompany ? label : isCase ? `Sponsoring ${label}` : `Current ${label}`,
        };
      default:
        return { ...field };
    }
  });

  return updatedFields;
};

export const reportingFieldsResetLabel = (fields: CustomTemplateFields[], category: TemplateCategory) => {
  if (!fields?.length) return fields;
  const isCase = category === TemplateCategory.Case;
  const isCompany = category === TemplateCategory.Company;

  const subStrToRemove = isCompany ? '' : isCase ? 'Sponsoring ' : 'Current ';

  const updatedFields: CustomTemplateFields[] = fields.map((field: CustomTemplateFields) => {
    const { fieldName, label } = field;
    switch (fieldName) {
      case 'companyLegalName':
      case 'displayCompanyName':
      case 'companyDbaName':
        return {
          ...field,
          label: label.replace(subStrToRemove, ''),
        };
      default:
        return { ...field };
    }
  });

  return updatedFields;
};

export const fieldsWithFilter = (field: CustomTemplateFields) => (
  (!!field?.filter) || (field.fieldType === reportingFieldTypes.DATE && !!field?.operator)
  || (field.fieldType === reportingFieldTypes.NUMBER && !!field?.value) || (field.fieldType === reportingFieldTypes.YEAR && !!field?.value)
);

export const toPascalCase = (str: string) => {
  if (!str?.length) return '--';

  return str.replace(/(\w)(\w*)/g, (_, first, rest) => first.toUpperCase() + rest.toLowerCase());
};

export const getParsedAddress = (address: string): StreetAddress | null => {
  try {
    let parsedAddress = null;
    if (address) {
      parsedAddress = JSON.parse(address) || '';
    }

    return parsedAddress;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Something went wrong while parsing address ', error);

    return null;
  }
};

export const getParsedSalary = (salary: string) => {
  try {
    if (!salary) return null;
    const parsedSalary = JSON.parse(salary) as Money;

    return parsedSalary;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Something went wrong while parsing salary ', error);

    return null;
  }
};

export const getAddressString = (address: StreetAddress, hideCountry: boolean = false): string => {
  if (!address) return '--'; // Return empty string if address is not provided

  const addressValues = address ? Object.values(address) : null;
  if (addressValues?.every((val) => !val)) return '--';

  const {
    address: addressPrimary,
    careOf,
    city,
    adminArea,
    unitNumber,
    unitType,
    country,
    postalCode,
    state,
  } = address;

  const stateProvince = city
    ? state || adminArea // If city and state, abbreviate
    : adminArea;

  let addressString = '';

  if (addressPrimary) addressString += `${addressPrimary}\n`;
  if (careOf) addressString += `${careOf}\n`;
  if (unitType || unitNumber) addressString += `${unitType ? resolvers.UnitType(unitType) : ''} ${unitNumber || ''}\n`;
  if (city || stateProvince) addressString += `${city || ''} ${city && stateProvince ? ',' : ''} ${stateProvince || ''}\n`;
  if (postalCode) addressString += `${postalCode || ''}\n`;
  if (!hideCountry && country) addressString += `${countryCodeResolver(country as CountryCode)}\n`;

  // Remove trailing comma and space
  return addressString.trim().replace(/,$/, '');
};

export const showReceiptData = (jsonString: string) => {
  let receiptDetails = [];

  try {
    const parsedArray = JSON.parse(jsonString);
    receiptDetails = Array.isArray(parsedArray) ? parsedArray : [parsedArray]; // Ensure array format
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Invalid JSON format', error);

    return '--';
  }

  if (!receiptDetails?.length) return '--';

  const groupedReceiptNumbers = groupReceiptsByReceiptNumber(receiptDetails);

  return Object.entries(groupedReceiptNumbers)
    .map(([receiptNumber, details]) => {
      const sortedStatuses = details
        .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime())
        .map((receipt) => receipt.currentCaseStatusText);

      return `${receiptNumber} - (${sortedStatuses.join(', ')})`;
    })
    .join(', ') || '--';
};

interface CurrencyData {
  period?: boolean;
  value?: Money | null;
}

export const getCurrencyStr = ({ period, value }: CurrencyData) => {
  if (value?.amount == null) {
    return null;
  }

  const [units, cents] = value.amount?.toString().split('.');

  return (
    `${resolvers.Currency((value as Money).currency as Currency)}${intComma(parseInt(units, 10))}${cents ? `.${cents.padEnd(2, '0').slice(0, 2)}` : ''} ${period && resolvers.CurrencyPeriod((value as Money).period || CurrencyPeriod.ANNUAL)}`
  );
};

export const imgUrlToFile = async (imgData: DocumentAPIResponse): Promise<File | null> => {
  try {
    const { signedUrl, fileName } = imgData;

    if (!signedUrl || !fileName) {
      return null;
    }

    const img = await fetch(signedUrl);
    const blob = await img.blob();
    const imgFile = new File([blob], fileName, { type: blob.type });

    return imgFile;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching image:', error);

    return null;
  }
};

export const arrayToStr = (list: string[]) => {
  if (!list?.length) return '';
  let str = '';
  // eslint-disable-next-line array-callback-return
  list.map((listItem: string, index: number) => {
    str = `${str}${listItem}${index + 1 !== listItem.length ? ', ' : ''}`;
  });

  return str;
};

export function showNamesWithComma(array: string[]): string {
  const names: string[] = array.map((item) => item.split(',')[0]);

  return names.join(', '); // Joining names with commas
}

export const requireIssueDate = ['PREVAILING_WAGE_DETERMINATION', 'ATTORNEY_CLIENT_AGREEMENT', 'ADVANCED_PAROLE', 'RE_ENTRY_PERMIT', 'DS_2019', 'GREEN_CARD'];

export const getFullName = (firstName: string, middleName: string, lastName: string) => `${firstName || ''}${middleName ? ` ${middleName}` : ''}${lastName ? ` ${lastName}` : ''}`;

export const getTitle = (user: Employer | TeamMember) => {
  const { firstname, middlename, lastname } = user || {};
  if (!firstname && !middlename && !lastname) return NO_NAME;

  return `${`${user.firstname || ''} ${user.middlename || ''} ${user.lastname || ''}`}`;
};

export const getUnqueIds = (list1: string[], list2: string[]) => {
  if (list1?.length) {
    const merged: string[] = list2?.length ? [...list1, ...list2] : [...list1];
    const unique = merged.filter((v, i, self) => i === self.indexOf(v));

    return [...unique];
  }

  return [...list2];
};

export const skipSortFields = ['keyFilingDates', 'fnWorkLocation', 'addressPrimary', 'addressSecondary', 'usResidentialAddress', 'fnSalary', 'caseSalary', 'grossRevenueForMostRecentFiscalYear', 'netRevenueForMostRecentFiscalYear'];

export const filterFNWithEMCompany = (relatives: Relative[], companies: String[]) => relatives.filter((relative) => !!companies.includes(relative?.companyID || ''));

export const isStringified = (str: string) => {
  try {
    JSON.parse(str); // Try to parse the string

    return true; // If successful, the string is valid JSON
  } catch (e) {
    return false; // If an error occurs, the string is not valid JSON
  }
};
